/**
 * Display helpers for the ledger. Money is integer cents and is never
 * abbreviated on a ledger ("1.05k" is an Ascend flaw, ledger-pay.md 8.1).
 */
import type { LedgerEntryView, LedgerOfficeSource, PaymentMethod } from '@velora/contracts/ledger';
import { formatMoney } from '@velora/ui';

/** $1,234.56; credits in parentheses when `accounting`. */
export const money = (cents: number, accounting = false) => formatMoney(cents, { accounting });

/** $1,235 for tiles and aging tiles. */
export const dollars = (cents: number) => formatMoney(cents, { showCents: false });

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Oct 9" in the current year, "Oct 9, 2025" otherwise. */
export function shortDate(iso: string, today?: string): string {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  const year = today ? Number(today.slice(0, 4)) : new Date().getFullYear();
  return `${MONTHS[m - 1] ?? ''} ${d}${y !== year ? `, ${y}` : ''}`;
}

/** "Friday, October 9, 2026". */
export function longDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(y, m - 1, d)));
}

export const PMS_NAMES: Readonly<Record<NonNullable<LedgerOfficeSource['pmsSystem']>, string>> = {
  opendental: 'Open Dental',
  dentrix: 'Dentrix',
  eaglesoft: 'Eaglesoft',
};

export function ownerName(s: LedgerOfficeSource): string {
  if (s.ledgerOwner === 'velora' || !s.pmsSystem) return 'Velora';
  return PMS_NAMES[s.pmsSystem];
}

const METHODS: Readonly<Record<PaymentMethod, string>> = {
  cash: 'Cash',
  check: 'Check',
  card: 'Card',
  ach: 'Bank transfer',
  financing: 'Financing',
  insurance_check: 'Insurance check',
  insurance_eft: 'Insurance EFT',
  other: 'Other',
};

export function methodName(m: PaymentMethod): string {
  return METHODS[m];
}

export const STATEMENT_MODES: Readonly<Record<string, string>> = {
  text: 'text',
  email: 'email',
  mail: 'mail',
  portal: 'portal',
  in_person: 'in person',
};

export function percent(value: number | null, digits = 1): string {
  if (value === null || !Number.isFinite(value)) return '-';
  return `${(value * 100).toFixed(digits)}%`;
}

export function plural(n: number, one: string, many = `${one}s`): string {
  return `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`;
}

/** "1 family differs", "3 families differ". */
export function familiesDiffer(n: number): string {
  return `${plural(n, 'family', 'families')} ${n === 1 ? 'differs' : 'differ'}`;
}

/** "1 payment ($60.00) to enter in Open Dental". */
export function pendingPmsLabel(payments: number, owner: string): string {
  return `${plural(payments, 'payment')} to enter in ${owner}`;
}

/** Brand names stored in caps by the processor ("AMERICAN_EXPRESS"). */
export function cardBrand(brand: string | null): string {
  if (!brand) return 'Card';
  return brand
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function relativeAge(iso: string | null, now = Date.now()): string {
  if (!iso) return 'never';
  const s = Math.max(0, Math.round((now - Date.parse(iso)) / 1000));
  if (s < 60) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h} h ago`;
  return `${Math.round(h / 24)} days ago`;
}

/**
 * How fresh an office's PMS ledger is, for the reconciliation panel:
 * "checked 3 min ago · last change 10 h ago". "Checked" is the ledger
 * reader's last successful read (it runs every few minutes and succeeds even
 * when nothing changed); "last change" is the newest ledger row change. The
 * connector-wide age (oldest cursor of every entity, e.g. a daily procedure
 * code read) is only the fallback for an API that does not send them.
 */
export function ledgerFreshness(
  source: {
    connectorSyncedAt: string | null;
    ledgerCheckedAt?: string | null | undefined;
    ledgerChangedAt?: string | null | undefined;
  },
  now = Date.now(),
): string {
  if (source.ledgerCheckedAt === undefined) {
    return `synced ${relativeAge(source.connectorSyncedAt, now)}`;
  }
  const checked = `checked ${relativeAge(source.ledgerCheckedAt, now)}`;
  return source.ledgerChangedAt
    ? `${checked} · last change ${relativeAge(source.ledgerChangedAt, now)}`
    : checked;
}

/**
 * The family's most recent patient payment, as a payment: the rows of one
 * payment split over several charges (same `paymentId`) are summed. Reversed
 * payments do not count.
 */
export function lastPayment(
  entries: readonly Pick<
    LedgerEntryView,
    'id' | 'type' | 'amountCents' | 'date' | 'paymentId' | 'reversedByEntryId' | 'reversesEntryId'
  >[],
): { date: string; cents: number } | null {
  const groups = new Map<string, { date: string; cents: number; order: number }>();
  entries.forEach((e, i) => {
    if (e.type !== 'payment' || e.amountCents >= 0 || e.reversedByEntryId || e.reversesEntryId)
      return;
    const k = e.paymentId ?? e.id;
    const g = groups.get(k) ?? { date: e.date, cents: 0, order: i };
    g.cents += -e.amountCents;
    if (e.date > g.date) g.date = e.date;
    g.order = Math.max(g.order, i);
    groups.set(k, g);
  });
  let best: { date: string; cents: number; order: number } | null = null;
  for (const g of groups.values())
    if (!best || g.date > best.date || (g.date === best.date && g.order > best.order)) best = g;
  return best ? { date: best.date, cents: best.cents } : null;
}
